import {
  Autocomplete,
  Box,
  Grid,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
  debounce,
} from '@mui/material'
import { KFieldLabel } from 'components/core/KFieldLabel'
import { useTheme } from '@mui/material/styles'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { GoogleMapPlace, Place } from 'models'
import { useTranslation } from 'react-i18next'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import parse from 'autosuggest-highlight/parse'
import { useGoogleMap } from 'hooks'

interface KAddressFieldProps
  extends Omit<TextFieldProps, 'onChange' | 'value'> {
  labelSize?: 'small' | 'medium' | 'large'
  value?: Place
  onChange?: (value?: Place) => void
}

interface KAddressFieldState {
  inputValue: string
  options: readonly GoogleMapPlace[]
  value: GoogleMapPlace | null | Place
}

export const KAddressField = forwardRef(
  (
    {
      label,
      labelSize,
      required,
      sx,
      value,
      error,
      helperText,
      onChange,
    }: KAddressFieldProps,
    ref,
  ) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const [state, setState] = useState<KAddressFieldState>({
      value: (value as GoogleMapPlace) ?? null,
      inputValue: (value as GoogleMapPlace)?.description ?? '',
      options: value ? [value as GoogleMapPlace] : [],
    })

    useEffect(() => {
      if (
        value?.place_id !== state.value?.place_id ||
        value?.saved_address_id !== (state.value as Place)?.saved_address_id
      ) {
        setState({
          value: (value as GoogleMapPlace) ?? null,
          inputValue: (value as GoogleMapPlace)?.description ?? '',
          options: value ? [value as GoogleMapPlace] : [],
        })
      }
    }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

    const { getPlacePredictions, fillAddressComponents } = useGoogleMap()

    const fetch = useMemo(
      () => debounce(getPlacePredictions, 400),
      [getPlacePredictions],
    )

    useEffect(() => {
      let active = true
      if (state.inputValue === '') {
        setState((s) => ({
          ...s,
          value: null,
          options: [],
        }))
        return undefined
      }

      fetch(state.inputValue, (results) => {
        if (active) {
          let newOptions: readonly GoogleMapPlace[] = []
          if (state.value) newOptions = [state.value as GoogleMapPlace]
          if (results) newOptions = [...newOptions, ...results]
          setState((s) => ({ ...s, options: newOptions }))
        }
      })

      return () => {
        active = false
      }
    }, [state.inputValue, fetch]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = async (newValue: GoogleMapPlace | null) => {
      if (newValue === null) {
        onChange?.(undefined)
        return
      }

      const place = await fillAddressComponents(newValue as Place)
      onChange?.(place)
    }

    return (
      <Autocomplete
        sx={{
          ...sx,
          '& .MuiOutlinedInput-root': {
            paddingLeft: 1.5,
            paddingY: 0,
            minWidth: 280,
            [theme.breakpoints.down('md')]: {
              minWidth: 230,
            },
          },
        }}
        getOptionLabel={(option) =>
          typeof option === 'string'
            ? option
            : (option as GoogleMapPlace).description
        }
        filterOptions={(x) => x}
        options={state.options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={state.value as GoogleMapPlace}
        noOptionsText={t('No matching address found')}
        onChange={(_event, newValue) => {
          setState((s) => ({
            ...s,
            value: newValue,
            options: newValue ? [newValue, ...s.options] : [...s.options],
          }))
          handleChange(newValue)
        }}
        onInputChange={(_event, newInputValue) => {
          setState((s) => ({ ...s, inputValue: newInputValue }))
          if (newInputValue === '') {
            handleChange(null)
          }
        }}
        renderInput={(params) => {
          return (
            <Stack spacing={1} sx={sx}>
              <KFieldLabel labelSize={labelSize} required={required}>
                {label}
              </KFieldLabel>
              <TextField
                inputRef={ref}
                {...params}
                error={error}
                helperText={helperText}
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'one-time-code',
                }}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    height: 56,
                    borderRadius: 4,
                    '&:hover': {
                      backgroundColor: theme.palette.grey['A100'],
                    },
                    [theme.breakpoints.down('sm')]: {
                      borderRadius: 3.5,
                      height: 52,
                      '& .MuiAutocomplete-input': {
                        padding: 1.5,
                      },
                    },
                  },
                }}
              />
            </Stack>
          )
        }}
        renderOption={(props, option: GoogleMapPlace) => {
          const matches =
            option.structured_formatting?.main_text_matched_substrings ?? []

          const parts = parse(
            option.structured_formatting?.main_text ?? '',
            matches.map((match: any) => [
              match.offset,
              match.offset + match.length,
            ]),
          )

          return (
            <li {...props} key={option.place_id}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid
                  item
                  sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
                >
                  {parts.map((part, index: number) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting?.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          )
        }}
      />
    )
  },
)
